import React from 'react';
import type {ReactElement, ReactNode} from 'react';

import CSSModules from 'react-css-modules';

import {useAssignFocus} from '../../hooks/useAssignFocus';
import {createBaseStyles} from '../textUtils';
import type {TextColours, TextTransforms, TextWeights} from '../textUtils';

import styles from './styles.module.scss';

export const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
export type HeadingTag = (typeof headingTags)[number];

export const headingSizes = [
  'elephant',
  'rhino',
  'leopard',
  'sheep',
  'cat',
  'hamster',
  'snail',
  'ant',
] as const;
export type HeadingSizes = (typeof headingSizes)[number];

type HeadingProps = {
  children: ReactNode;
  /** HTML tag */
  as: HeadingTag | 'legacy-span';
  /** elephant: 48px, rhino: 36px, leopard: 28px, sheep: 24px, cat: 20px, hamster: 18px, snail: 16px, ant: 14px */
  size: HeadingSizes;
  /** Text colour
   * @default textPrimary
   */
  colour?: TextColours;
  /** Font weight
   * @default regular
   * */
  /** regular: 400, medium: 500, semiBold: 600, bold: 700 */
  weight?: TextWeights;
  /** Max number of lines.
   * Overflow text is truncated with an ellipsis ...
   */
  maxLines?: number;
  inline?: boolean;
  /** @default none */
  textTransform?: TextTransforms;
  /** HTML ID */
  id?: string;
  /** Any valid CSS colour value */
  overrideColour?: string;
  dataAutomation?: string;
  /**
   * If true, the heading will be focused when the component is mounted.
   * Primarily useful for accessibility purposes upon screen change.
   * @default false
   */
  accessibilityAutoFocus?: boolean;
};

const Heading = ({
  children,
  as,
  size,
  colour = 'textPrimary',
  weight = 'regular',
  maxLines,
  inline,
  textTransform = 'none',
  id,
  overrideColour,
  dataAutomation,
  accessibilityAutoFocus = false,
}: HeadingProps): ReactElement => {
  const Tag = as === 'legacy-span' ? 'span' : as;
  const {focusRef} = useAssignFocus<HTMLHeadingElement>({
    disabled: !accessibilityAutoFocus,
  });
  const styleName = `heading ${size} ${colour} ${weight} ${textTransform}`;
  const style = createBaseStyles({
    maxLines,
    inline,
    overrideColour,
  });

  const Component = (
    <Tag
      styleName={styleName}
      data-automation={dataAutomation}
      id={id}
      style={style}
      ref={focusRef}
      tabIndex={accessibilityAutoFocus ? -1 : undefined}
    >
      {children}
    </Tag>
  );

  return maxLines ? <div>{Component}</div> : Component;
};

export default CSSModules(Heading, styles, {allowMultiple: true});
